import { Heart, TrendingDown, Calendar, Target, Flame, Activity, AlertTriangle, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ChurnRiskScore, getRiskColor, getRiskBgColor } from '@/lib/churn-risk';
import { cn } from '@/lib/utils';

interface ChurnRiskCardProps {
  riskScore: ChurnRiskScore;
  className?: string;
}

export function ChurnRiskCard({ riskScore, className }: ChurnRiskCardProps) {
  const { score, level, factors, primaryReason, recommendations } = riskScore;

  const activeFactors = Object.entries(factors)
    .filter(([_, active]) => active)
    .map(([key]) => key);

  const factorLabels: Record<string, { label: string; icon: typeof Heart }> = {
    sessionFrequencyDecline: { label: 'Training less often lately', icon: TrendingDown },
    missedSessionsIncreasing: { label: 'Cancelling more sessions', icon: Calendar },
    packageEndingSoon: { label: 'Package almost finished', icon: Calendar },
    noRenewalScheduled: { label: 'No renewal booked yet', icon: Target },
    longGapSinceLastSession: { label: "Haven't trained in a while", icon: Calendar },
    streakBroken: { label: 'Lost their workout streak', icon: Flame },
    lowEngagement: { label: 'Not logging workouts', icon: Activity },
    resultsStalling: { label: 'Progress has plateaued', icon: BarChart3 },
  };

  const statusLabels: Record<string, string> = {
    critical: 'Needs You Now',
    high: 'Check In Soon',
    medium: 'Keep an Eye On',
    low: 'On Track',
  };

  return (
    <Card className={cn('border', getRiskBgColor(level), className)}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-lg">
          <span className="flex items-center gap-2">
            <Heart className={cn('h-5 w-5', getRiskColor(level))} />
            Client Check-In
          </span>
          <span className={cn('text-2xl font-bold', getRiskColor(level))}>
            {score}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="flex items-center justify-between text-sm mb-1">
            <span className="text-muted-foreground">Status</span>
            <span className={cn('font-medium', getRiskColor(level))}>
              {statusLabels[level]}
            </span>
          </div>
          <Progress
            value={score}
            className="h-2"
          />
        </div>

        <div>
          <p className="text-sm font-medium mb-2">What's Going On</p>
          <p className="text-sm text-muted-foreground">{primaryReason}</p>
        </div>

        {activeFactors.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">Signs to Watch</p>
            <div className="space-y-1">
              {activeFactors.map(factor => {
                const { label, icon: Icon } = factorLabels[factor] || { 
                  label: factor, 
                  icon: AlertTriangle 
                };
                return (
                  <div 
                    key={factor} 
                    className="flex items-center gap-2 text-sm text-muted-foreground"
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {recommendations.length > 0 && (
          <div>
            <p className="text-sm font-medium mb-2">How You Can Help</p>
            <ul className="space-y-1">
              {recommendations.map((rec, i) => (
                <li 
                  key={i} 
                  className="text-sm text-muted-foreground flex items-start gap-2"
                >
                  <span className="text-primary mt-0.5">•</span>
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
